import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { SUPPORTER_VOICE_ENTRY_PARAM } from '#src/app/shared/constants';
import { useClearedQueryParamValue } from '#src/app/shared/hooks/location';
import { isCommentAfterVideoEntry } from '#src/app/shared/utils/supporterVoicesFlow';

export type VideoFirstFlowContextValue = {
	data: {
		/**
		 * Deliberately not `shareSupporterVoiceSuccess`, which means "the post-comment screen is showing"
		 * and is cleared the moment the supporter opens the camera from it.
		 */
		hasCommented: boolean;
		/** True only when the page was entered from the video confirmation. */
		hasRecordedVideo: boolean;
	};
	actions: {
		markCommented: () => void;
	};
};

/**
 * Defaulted rather than mandatory: "neither has happened" is a meaningful answer, so components
 * outside the flow need no decorator. Exported for the tests-helper only.
 *
 * @knip-ignore
 */
export const VideoFirstFlowContext = createContext<VideoFirstFlowContextValue>({
	data: { hasCommented: false, hasRecordedVideo: false },
	actions: { markCommented: () => undefined },
});

export function useVideoFirstFlow(): VideoFirstFlowContextValue {
	return useContext(VideoFirstFlowContext);
}

/** Mounted for every arm, control included; the arms differ only in who reads it. */
export const VideoFirstFlowProvider: React.FC<PropsWithChildren> = ({ children }) => {
	// Cleared from the URL as it is read, so a reload cannot re-enter a step already left - which also
	// means a reload on the comment step forgets the video. Durable needs a server-side field.
	const entry = useClearedQueryParamValue(SUPPORTER_VOICE_ENTRY_PARAM);
	const [hasRecordedVideo] = useState(() => isCommentAfterVideoEntry(entry));
	const [hasCommented, setHasCommented] = useState(false);

	const markCommented = useCallback(() => setHasCommented(true), []);

	const value = useMemo<VideoFirstFlowContextValue>(
		() => ({ data: { hasCommented, hasRecordedVideo }, actions: { markCommented } }),
		[hasCommented, hasRecordedVideo, markCommented],
	);

	return <VideoFirstFlowContext.Provider value={value}>{children}</VideoFirstFlowContext.Provider>;
};
